import { AgGridReact } from "ag-grid-react"
import React, { useMemo } from "react"
import { Fundamentals } from "../../../api/fundamentalsApi"
import { useAgGrid } from "../PortfolioManagement/components/hooks/useAgGrid"

interface FundamentalsGridProps {
  fundamentals: Fundamentals | undefined
  selectedSource: string
}

const FundamentalsGrid: React.FC<FundamentalsGridProps> = ({ fundamentals, selectedSource }) => {
  const columns = useMemo(() => {
    const allColumns = fundamentals?.data?.columns || []

    return allColumns.filter(column => {
      // Keep non-data columns (like metric column)
      if (!column.field || column.field === "metric") {
        return true
      }

      // Always show actuals regardless of source
      if (column.field.startsWith("A_")) {
        return true
      }

      // For estimates, only show those matching the selected source
      if (column.field.startsWith("E_")) {
        return column.field.includes(selectedSource)
      }

      return false
    })
  }, [fundamentals?.data?.columns, selectedSource])

  const dynamicScrollColumn = useMemo(() => {
    if (!columns || columns.length === 0) {
      return undefined
    }

    // Filter to get only data columns (exclude metric, path, etc.)
    const dataColumns = columns.filter(col => col.field && col.field !== "metric")

    // Find E columns matching the selected source
    const sourceEstimates = dataColumns.filter(
      col => col.field?.startsWith("E_") && col.field?.includes(selectedSource),
    )

    if (sourceEstimates.length > 0) {
      // Scroll to first estimate for this source
      return sourceEstimates[0].field
    }

    // Fallback to latest actuals if no estimates for this source
    const actuals = dataColumns.filter(col => col.field?.startsWith("A_"))
    if (actuals.length > 0) {
      return actuals[actuals.length - 1].field // Latest actual
    }

    return undefined
  }, [columns, selectedSource])

  const { gridRef, gridOptions } = useAgGrid({
    columns,
    initialScrollColumn: dynamicScrollColumn,
  })

  return (
    <AgGridReact
      ref={gridRef}
      rowData={fundamentals?.data?.rows}
      {...gridOptions}
      treeData={true}
      groupDefaultExpanded={-1} // expand all levels
      getDataPath={data => data.path}
      autoGroupColumnDef={{
        headerName: "Metric",
        minWidth: 300,
        pinned: "left",
        cellRendererParams: {
          suppressCount: true,
        },
      }}
      rowHeight={25}
      headerHeight={30}
      overlayLoadingTemplate={"Select a stock to view fundamentals"}
    />
  )
}

export default FundamentalsGrid
